import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { regenerateInterview } from "./regenerateInterview";
import toast from "react-hot-toast";
import { resetProgress, setNeedsRefetch, setProgress, selectInterviewState } from "@/redux/reducers/InterviewReducer";
import useLiveResource from "@/hooks/useLiveResource";
import { CandidateService } from "@/services/candidateService";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { MODULES } from "@/permissions/modules";

const useInterviewGeneration = (candidateId, applicationId, configuredTypes = []) => {
  const dispatch = useDispatch();
  // Without the module nothing can be generating, so the status sweep below
  // has no question to ask. The panel still renders whatever is stored.
  const { isEnabled: moduleEnabled } = useModuleAccess(MODULES.AI_INTERVIEW_QUESTIONS);

  /**
   * Ask the server, per configured type, whether a generation is running.
   *
   * This is also how a live event lands: `interview_question.*` carries the
   * candidate and the application, and no question type — the reducer keys
   * progress by type, so the server's own per-type answer is the only thing
   * that can say which spinner to raise and which to clear.
   */
  const runStatusSweep = useCallback(() => {
    if (!applicationId || !moduleEnabled || configuredTypes.length === 0) return;
    const statusChecks = configuredTypes.map((t) =>
      CandidateService.getInterviewStatus(applicationId, t.value)
        .then((res) => ({ type: t.value, status: res?.status }))
        .catch(() => ({ type: t.value, status: null })),
    );
    Promise.all(statusChecks).then((results) => {
      for (const { type, status } of results) {
        if (status === "processing") {
          dispatch(
            setProgress({
              applicationId,
              questionType: type,
              progress: { percentage: 0, message: "Generation in progress..." },
            }),
          );
        } else if (status) {
          // The run this type was showing a spinner for has finished — which
          // type that was is exactly what the event could not say.
          dispatch(resetProgress({ applicationId, questionType: type }));
        }
      }
    });
  }, [applicationId, configuredTypes, dispatch, moduleEnabled]);

  useEffect(() => {
    runStatusSweep();
  }, [runStatusSweep]);

  const state = useSelector(selectInterviewState(applicationId));
  const anyGenerating = Object.values(state).some((v) => v?.regenProgress);
  const anyGeneratingRef = useRef(anyGenerating);
  anyGeneratingRef.current = anyGenerating;

  useLiveResource({
    keys: `interview_question:${candidateId}`,
    enabled: Boolean(candidateId && applicationId),
    // A candidate can hold several applications; this tab is one of them.
    match: (event) => String(event.parent?.application) === String(applicationId),
    onEvent: (event) => {
      if (event.event !== "interview_question.failed") return;
      // `user_id` is gone from the payload, so "the person who pressed the
      // button" cannot be identified. A tab with a generation in flight is the
      // closest honest stand-in.
      if (!anyGeneratingRef.current) return;
      toast.error("Question generation failed. Please try again.");
    },
    onChange: () => {
      runStatusSweep();
      dispatch(setNeedsRefetch({ applicationId, value: true }));
    },
  });

const handleRegenerate = useCallback((questionType, hasQuestions, seniorityLevel) => {
  dispatch(regenerateInterview(candidateId, applicationId, hasQuestions, questionType, seniorityLevel));
}, [candidateId, applicationId, dispatch]);

const clearRefetch = useCallback(() => {
  dispatch(setNeedsRefetch({ applicationId, value: false }));
}, [applicationId, dispatch]);

  return {
    state,
    anyGenerating,
    needsRefetch: state.needsRefetch,
    handleRegenerate,
    clearRefetch,
  };
};

export default useInterviewGeneration;
